import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import type { User, UserRole } from '../../types';

const UsersTab: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [deletingUser, setDeletingUser] = useState<User | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const queryClient = useQueryClient();

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: usersAPI.getUsers,
  });

  // Filter users based on search query
  const filteredUsers = useMemo(() => {
    if (!searchQuery.trim()) {
      return users;
    }

    const query = searchQuery.trim().toLowerCase();
    return users.filter((user: User) => {
      const username = (user.username || '').toLowerCase();
      const name = (user.name || '').toLowerCase();
      const surname = (user.surname || '').toLowerCase();
      const fullName = `${name} ${surname}`.trim().toLowerCase();
      
      return (
        username.includes(query) ||
        name.includes(query) ||
        surname.includes(query) ||
        fullName.includes(query)
      );
    });
  }, [users, searchQuery]);

  const createUserMutation = useMutation({
    mutationFn: usersAPI.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowCreateForm(false);
      alert('User created successfully!');
    },
    onError: (error: any) => {
      alert(`Error creating user: ${error.response?.data?.error || error.message}`);
    },
  });

  const bulkCreateMutation = useMutation({
    mutationFn: usersAPI.bulkCreateUsers,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowUploadForm(false);
      alert(`Upload complete: ${data.created} users created, ${data.failed} failed.`);
    },
    onError: (error: any) => {
      alert(`Error uploading file: ${error.response?.data?.error || error.message}`);
    },
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => usersAPI.updateUser(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setEditingUser(null);
      alert('User updated successfully!');
    },
    onError: (error: any) => {
      alert(`Error updating user: ${error.response?.data?.error || error.message}`);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: usersAPI.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeletingUser(null);
      alert('User deleted successfully!');
    },
    onError: (error: any) => {
      alert(`Error deleting user: ${error.response?.data?.error || error.message}`);
    },
  });

  const handleCreateUser = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      username: formData.get('username') as string,
      password: formData.get('password') as string,
      name: formData.get('name') as string || undefined,
      surname: formData.get('surname') as string || undefined,
      role: (formData.get('role') as UserRole) || 'USER',
    };
    createUserMutation.mutate(data);
  };

  const handleFileUpload = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const file = formData.get('file') as File;
    if (file) {
      bulkCreateMutation.mutate(file);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await usersAPI.downloadTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'user-upload-template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      alert(`Error downloading template: ${error.response?.data?.error || error.message}`);
    }
  };

  const handleEditUser = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!editingUser) return;

    const formData = new FormData(e.currentTarget);
    const data: { username?: string; password?: string; name?: string; surname?: string; role?: UserRole } = {};

    const newUsername = formData.get('username') as string;
    if (newUsername && newUsername !== editingUser.username) {
      data.username = newUsername;
    }

    const newName = formData.get('name') as string;
    const currentName = editingUser.name || '';
    if (newName !== currentName) {
      data.name = newName || undefined;
    }

    const newSurname = formData.get('surname') as string;
    const currentSurname = editingUser.surname || '';
    if (newSurname !== currentSurname) {
      data.surname = newSurname || undefined;
    }

    const newPassword = formData.get('password') as string;
    if (newPassword && newPassword.trim().length > 0) {
      data.password = newPassword;
    }

    const newRole = formData.get('role') as UserRole;
    if (newRole && newRole !== editingUser.role) {
      data.role = newRole;
    }

    // Only update if there are changes
    if (Object.keys(data).length > 0) {
      updateUserMutation.mutate({ id: editingUser.id, data });
    } else {
      setEditingUser(null);
    }
  };

  const handleDeleteClick = (user: User) => {
    setDeletingUser(user);
  };

  const handleDeleteConfirm = () => {
    if (deletingUser) {
      deleteUserMutation.mutate(deletingUser.id);
    }
  };

  const canDeleteUser = (user: User): boolean => {
    // Admin cannot delete themselves
    if (user.id === currentUser?.id) {
      return false;
    }
    return true;
  };

  const getDeleteWarning = (user: User): string => {
    if (user.id === currentUser?.id) {
      return 'You cannot delete your own account.';
    }
    if (user.role === 'ADMIN') {
      // Check admin count from all users, not filtered
      const adminCount = users.filter((u) => u.role === 'ADMIN').length;
      if (adminCount <= 1) {
        return 'Cannot delete the last admin.';
      }
      return 'Warning: This user is an admin. Are you sure you want to delete them?';
    }
    return `Are you sure you want to delete user "${user.username}"? This action cannot be undone.`;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-800">Users Management</h2>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setShowUploadForm(!showUploadForm);
              setShowCreateForm(false);
              setEditingUser(null);
            }}
            className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700"
          >
            {showUploadForm ? 'Cancel Upload' : 'Upload Excel'}
          </button>
          <button
            onClick={() => {
              setShowCreateForm(!showCreateForm);
              setShowUploadForm(false);
              setEditingUser(null);
            }}
            className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700"
          >
            {showCreateForm ? 'Cancel' : 'Create User'}
          </button>
        </div>
      </div>

      {/* Excel Upload Form */}
      {showUploadForm && (
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <h3 className="text-xl font-semibold mb-4">Upload Users from Excel</h3>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-gray-700 mb-2">
              <strong>Format Requirements:</strong>
            </p>
            <ul className="text-sm text-gray-600 list-disc list-inside mb-2 space-y-1">
              <li>Excel file must have columns: <strong>Name</strong> and <strong>Surname</strong> (case-sensitive)</li>
              <li>Each row should contain one user's information</li>
              <li>Usernames will be auto-generated from name and surname</li>
              <li>Passwords will be auto-generated for each user</li>
            </ul>
            <button
              type="button"
              onClick={handleDownloadTemplate}
              className="mt-2 text-sm text-primary-600 hover:text-primary-700 font-medium underline"
            >
              ↓ Download Excel Template
            </button>
          </div>
          <form onSubmit={handleFileUpload} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Excel File (.xlsx or .xls)
              </label>
              <input
                type="file"
                name="file"
                accept=".xlsx,.xls"
                required
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
              />
            </div>
            <button
              type="submit"
              disabled={bulkCreateMutation.isPending}
              className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {bulkCreateMutation.isPending ? 'Uploading...' : 'Upload & Create Users'}
            </button>
          </form>
        </div>
      )}

      {/* Single User Creation Form */}
      {showCreateForm && (
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <h3 className="text-xl font-semibold mb-4">Create New User</h3>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  name="username"
                  required
                  className="w-full border rounded px-3 py-2"
                  placeholder="johndoe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  required
                  minLength={6}
                  className="w-full border rounded px-3 py-2"
                  placeholder="••••••"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  name="name"
                  className="w-full border rounded px-3 py-2"
                  placeholder="John"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Surname
                </label>
                <input
                  type="text"
                  name="surname"
                  className="w-full border rounded px-3 py-2"
                  placeholder="Doe"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select name="role" className="w-full border rounded px-3 py-2" defaultValue="USER">
                  <option value="USER">User</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>
            </div>
            <button
              type="submit"
              disabled={createUserMutation.isPending}
              className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {createUserMutation.isPending ? 'Creating...' : 'Create User'}
            </button>
          </form>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b flex justify-between items-center">
              <h3 className="text-xl font-semibold">Edit User: {editingUser.username}</h3>
              <button
                onClick={() => setEditingUser(null)}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleEditUser} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  name="username"
                  required
                  className="w-full border rounded px-3 py-2"
                  defaultValue={editingUser.username}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    className="w-full border rounded px-3 py-2"
                    defaultValue={editingUser.name || ''}
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Surname
                  </label>
                  <input
                    type="text"
                    name="surname"
                    className="w-full border rounded px-3 py-2"
                    defaultValue={editingUser.surname || ''}
                    placeholder="Doe"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password (leave empty to keep current)
                </label>
                <input
                  type="password"
                  name="password"
                  className="w-full border rounded px-3 py-2"
                  placeholder="••••••"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Only enter a new password if you want to change it
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  name="role"
                  className="w-full border rounded px-3 py-2"
                  defaultValue={editingUser.role}
                >
                  <option value="USER">User</option>
                  <option value="ADMIN">Admin</option>
                </select>
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={updateUserMutation.isPending}
                  className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 disabled:opacity-50"
                >
                  {updateUserMutation.isPending ? 'Updating...' : 'Update User'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditingUser(null)}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b">
              <h3 className="text-xl font-semibold text-accent-600">Delete User</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-700 mb-4">{getDeleteWarning(deletingUser)}</p>
              {deletingUser.role === 'ADMIN' && deletingUser.id !== currentUser?.id && (
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded mb-4">
                  <strong>Admin User:</strong> This user has administrative privileges. Deleting them will remove their access to admin features.
                </div>
              )}
              <div className="flex gap-2">
                <button
                  onClick={handleDeleteConfirm}
                  disabled={!canDeleteUser(deletingUser) || deleteUserMutation.isPending}
                  className="bg-accent-600 text-white px-4 py-2 rounded hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteUserMutation.isPending ? 'Deleting...' : 'Delete User'}
                </button>
                <button
                  onClick={() => setDeletingUser(null)}
                  className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search users by username, name, or surname..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full border rounded px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="text-gray-500 hover:text-gray-700 text-sm font-medium"
              >
                Clear
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="text-xs text-gray-500 mt-2">
              Showing {filteredUsers.length} of {users.length} user{users.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Username
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Surname
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Role
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Bookings
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Created
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  Loading users...
                </td>
              </tr>
            ) : filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-gray-500">
                  {searchQuery ? `No users found matching "${searchQuery}"` : 'No users found.'}
                </td>
              </tr>
            ) : (
              filteredUsers.map((user: any) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {user.username}
                    {user.id === currentUser?.id && (
                      <span className="ml-2 text-xs text-gray-500">(You)</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.surname || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        user.role === 'ADMIN'
                          ? 'bg-accent-100 text-accent-800'
                          : 'bg-primary-100 text-primary-800'
                      }`}
                    >
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user._count?.bookings || 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingUser(user)}
                        className="border border-primary-600 text-primary-600 hover:bg-primary-50 px-3 py-1 rounded text-sm font-medium transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(user)}
                        disabled={!canDeleteUser(user)}
                        className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                          canDeleteUser(user)
                            ? 'text-accent-600 hover:text-accent-700'
                            : 'text-gray-400 cursor-not-allowed'
                        }`}
                        title={
                          !canDeleteUser(user)
                            ? "You cannot delete your own account"
                            : undefined
                        }
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UsersTab;
